import { useEffect, useState, type FormEvent } from 'react';
import { rememberReturnTo, useAuthStore } from '../store/authStore';

export function LoginScreen() {
  const options = useAuthStore((s) => s.options);
  const loadOptions = useAuthStore((s) => s.loadOptions);
  useEffect(() => { if (!options) void loadOptions(); }, [options, loadOptions]);
  if (!options) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-page">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Chronic<span className="text-brand">Guru</span></h1>
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-brand" />
      </div>
    );
  }
  return options.passwordSignIn ? <PasswordLogin /> : <TeamLogin teamUrl={options.teamUrl} />;
}

/** ONE LOGIN (v1.94.0). Allyson, 2026-09-29: Projects folds into CG Team, so
 *  there is no password here. CG Team signs people in and its Projects tile
 *  brings them back. Any error from a failed hand-off (switched off, let go)
 *  still shows, so nobody is left staring at a button with no reason. */
function TeamLogin({ teamUrl }: { teamUrl: string }) {
  const error = useAuthStore((s) => s.error);
  return (
    <div className="flex h-dvh flex-col bg-page">
      <header className="flex shrink-0 flex-col items-center pt-12 pb-6">
        <h1 className="text-3xl font-bold tracking-tight text-ink">
          Chronic<span className="text-brand">Guru</span>
        </h1>
        <span className="mt-1 text-xs font-medium uppercase tracking-[0.22em] text-muted">
          Projects
        </span>
      </header>

      <div className="flex flex-1 flex-col items-center justify-start px-6">
        <div className="animate-fade-in w-full max-w-sm rounded-2xl border border-line bg-canvas px-6 py-6 text-center">
          <h2 className="mb-2 text-lg font-semibold text-ink">Open Projects from CG Team</h2>
          <p className="mb-5 text-sm text-muted">Sign in to CG Team, then tap Projects.</p>

          {error && (
            <p role="alert" className="mb-4 rounded-xl border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}

          <a
            href={teamUrl}
            // Keep the page they were trying to reach (a task link from an email)
            // so the Team tile lands them on it, not on the home page.
            onClick={() => rememberReturnTo(window.location.hash)}
            className="flex h-12 w-full items-center justify-center rounded-xl bg-brand text-sm font-semibold text-page transition-all duration-150 hover:brightness-110 active:scale-[0.98]"
          >
            Go to CG Team
          </a>
        </div>
      </div>

      <footer className="shrink-0 py-4 text-center text-xs text-faint">Chronic Guru Projects v{__APP_VERSION__}</footer>
    </div>
  );
}

/** The old password door. Only shown when the server says it is open
 *  (ONE_LOGIN=off, the rollback). */
function PasswordLogin() {
  const login = useAuthStore((s) => s.login);
  const loggingIn = useAuthStore((s) => s.loggingIn);
  const error = useAuthStore((s) => s.error);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Always starts hidden — a page that remembered "shown" would expose the next
  // person's password on a shared store tablet.
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await login(email.trim(), password);
  };

  return (
    // Built on the SAME earth-tone tokens as the app. This screen used to run on
    // raw bg-white/5 + text-white/NN, so the first thing every user saw was a
    // different visual language than the product behind it — and any future theme
    // change would have silently skipped it.
    <div className="flex h-dvh flex-col bg-page">
      <header className="flex shrink-0 flex-col items-center pt-12 pb-6">
        <h1 className="text-3xl font-bold tracking-tight text-ink">
          Chronic<span className="text-brand">Guru</span>
        </h1>
        <span className="mt-1 text-xs font-medium uppercase tracking-[0.22em] text-muted">
          Projects
        </span>
      </header>

      <div className="flex flex-1 flex-col items-center justify-start px-6">
        <form
          onSubmit={onSubmit}
          className="animate-fade-in w-full max-w-sm rounded-2xl border border-line bg-canvas px-6 py-6"
        >
          <h2 className="mb-6 text-center text-lg font-semibold text-ink">Sign in</h2>

          <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted">Email</label>
          <input
            id="login-email"
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@chronicguru.com"
            autoComplete="username"
            className="mb-4 h-12 w-full rounded-xl border border-line bg-page px-4 text-sm font-medium text-ink outline-none transition-colors placeholder:text-faint focus:border-brand focus:ring-1 focus:ring-brand"
          />

          <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted">Password</label>
          {/* Show/hide. On a phone, typing a long password blind and getting one
              unhelpful "Invalid email or password" back is the single most annoying
              way to fail to sign in. */}
          <div className="relative mb-4">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="h-12 w-full rounded-xl border border-line bg-page pl-4 pr-12 text-sm font-medium text-ink outline-none transition-colors placeholder:text-faint focus:border-brand focus:ring-1 focus:ring-brand"
            />
            <button
              // type="button" is load-bearing: inside a <form>, a button without it
              // defaults to submit, so revealing the password would ALSO fire a
              // half-typed sign-in attempt — and burn one of 8 tries per account
              // against the login throttle.
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              title={showPassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-muted transition-colors hover:text-ink focus:outline-none focus-visible:text-ink"
            >
              {showPassword ? (
                // eye-off
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.61 6.61A18.4 18.4 0 0 0 2 12s3 8 10 8a9 9 0 0 0 5.39-1.61" />
                  <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                  <path d="m2 2 20 20" />
                </svg>
              ) : (
                // eye
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          {error && (
            <p role="alert" className="mb-3 rounded-xl border border-danger/30 bg-danger/10 px-3 py-2 text-center text-xs text-danger">
              {error}
            </p>
          )}

          <p className="mb-3 text-center">
            <a
              href="https://chronicguru.odoo.com/web/reset_password"
              target="_blank"
              rel="noopener"
              className="text-xs text-muted underline-offset-2 hover:text-ink hover:underline"
            >
              Forgot password?
            </a>
          </p>

          <button
            type="submit"
            disabled={loggingIn || !email || !password}
            className="mt-1 h-12 w-full rounded-xl bg-brand text-sm font-semibold text-page transition-all duration-150 hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loggingIn ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="mt-4 text-center text-xs text-faint">
            Use the same email and password as your other work tools.
          </p>
        </form>
      </div>

      <footer className="shrink-0 py-4 text-center text-xs text-faint">Chronic Guru Projects v{__APP_VERSION__}</footer>
    </div>
  );
}
